import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  RefreshCw,
  Search,
  ShoppingCart,
  Package,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Zap,
} from 'lucide-react';
import { Button } from '../ui/index.js';
import { cn } from '../ui/cn.js';
import {
  fetchSalesByBrand,
  fetchSalesSyncStatus,
  triggerOrdersSync,
  type BrandSalesRow,
} from '../api.js';

function formatTnd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return (
    value.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }) + ' TND'
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'à l’instant';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `il y a ${diffHours} h`;
  const diffDays = Math.floor(diffHours / 24);
  return `il y a ${diffDays} j`;
}

export function SalesByBrandPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [periodMode, setPeriodMode] = useState<'preset' | 'custom'>('preset');
  const [days, setDays] = useState<number>(30);
  const [fromDate, setFromDate] = useState<string>('2023-03-01');
  const [toDate, setToDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [sort, setSort] = useState<'qty' | 'revenue' | 'orders' | 'products'>('qty');
  const [search, setSearch] = useState<string>('');
  const [includeProcessing, setIncludeProcessing] = useState<boolean>(true);
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());

  // Live sync diagnostics
  const syncStatusQuery = useQuery({
    queryKey: ['sales-sync-status'],
    queryFn: fetchSalesSyncStatus,
    refetchInterval: 30_000,
  });

  // Trigger manual sync mutation
  const syncMutation = useMutation({
    mutationFn: triggerOrdersSync,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales-sync-status'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-by-brand'] });
    },
  });

  // Sales by brand query
  const brandQuery = useQuery({
    queryKey: [
      'sales-by-brand',
      periodMode,
      days,
      fromDate,
      toDate,
      sort,
      search,
      includeProcessing,
    ],
    queryFn: () =>
      fetchSalesByBrand({
        days: periodMode === 'preset' ? days : undefined,
        from: periodMode === 'custom' ? fromDate : undefined,
        to: periodMode === 'custom' ? toDate : undefined,
        sort,
        search: search.trim() !== '' ? search.trim() : undefined,
        includeProcessing,
        limit: 200,
      }),
    refetchInterval: 5 * 60_000,
  });

  const toggleBrandExpand = (brand: string): void => {
    setExpandedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };

  const expandAll = (): void => {
    if (!brandQuery.data?.brands) return;
    setExpandedBrands(new Set(brandQuery.data.brands.map((b) => b.brand)));
  };

  const collapseAll = (): void => {
    setExpandedBrands(new Set());
  };

  const data = brandQuery.data;
  const brands = data?.brands ?? [];
  const totals = data?.totals ?? { units: 0, revenue: 0, orders: 0, brandsCount: 0 };
  const maxUnits = brands.length > 0 ? Math.max(...brands.map((b) => b.qtySold)) : 1;
  const syncStatus = syncStatusQuery.data;

  // CSV Export
  const exportCsv = (): void => {
    const header = [
      'Rang',
      'Marque',
      'Unités Vendues',
      'CA TTC',
      'Part de Marché (%)',
      'Commandes',
      'Panier Moyen TTC',
      'Références Vendues',
      'Total Références Catalogue',
      'Stock Total Site',
      'Dernière Vente',
    ].join(';');

    const lines = brands.map((b, idx) =>
      [
        idx + 1,
        `"${b.brand.replace(/"/g, '""')}"`,
        b.qtySold,
        b.revenueTtc,
        (b.revenueShare * 100).toFixed(2) + '%',
        b.ordersCount,
        b.averageOrderValue,
        b.distinctProductsSold,
        b.catalogProductsCount,
        b.stockQtyTotal ?? '',
        b.lastSoldAt ? b.lastSoldAt.slice(0, 10) : '',
      ].join(';'),
    );

    const blob = new Blob(['\uFEFF' + [header, ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventes-par-marque-${days}j.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const topBrandByUnits = brands.length > 0 ? brands[0] : null;
  const topBrandByRev =
    brands.length > 0 ? [...brands].sort((a, b) => b.revenueTtc - a.revenueTtc)[0] : null;

  return (
    <div className="space-y-5 pb-16">
      {/* Top Header */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-amber-100 border border-amber-600/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-800">
            S15b · Grossistes B2B
          </span>
          <span className="text-xs text-ink-ghost">·</span>
          <span className="text-xs text-ink-soft">Palmarès & Rotation par Marque</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink font-display">
              Ventes par Marque
            </h1>
            <p className="text-sm text-ink-soft">
              Classement consolidé des marques les plus vendues, parts de marché, panier moyen et top
              produits phares depuis les commandes en direct.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/sales-stats">
              <Button variant="outline" size="sm">
                <ShoppingCart className="h-4 w-4" />
                Vue par Produit
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              disabled={brandQuery.isFetching}
              onClick={() => void brandQuery.refetch()}
            >
              <RefreshCw className={cn('h-4 w-4', brandQuery.isFetching && 'animate-spin')} />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      {/* Sync Health & Diagnostic Banner */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-2xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'grid h-10 w-10 place-items-center rounded-lg border',
                syncStatus?.isSyncHealthy
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-600/20'
                  : 'bg-amber-50 text-amber-700 border-amber-600/20',
              )}
            >
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs text-ink">
                  Synchronisation Commandes WooCommerce
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.2 text-[10px] font-bold uppercase tracking-wider',
                    syncStatus?.isSyncHealthy
                      ? 'bg-emerald-100/80 text-emerald-800 border border-emerald-600/20'
                      : 'bg-amber-100/80 text-amber-800 border border-amber-600/20',
                  )}
                >
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      syncStatus?.isSyncHealthy ? 'bg-emerald-600 animate-pulse' : 'bg-amber-600',
                    )}
                  />
                  {syncStatus?.isSyncHealthy ? 'Active (toutes les 15 min)' : 'En attente'}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-ink-soft">
                {syncStatus?.lastOrder ? (
                  <>
                    Dernière commande :{' '}
                    <strong className="text-ink">#{syncStatus.lastOrder.number}</strong> ({timeAgo(syncStatus.lastOrder.dateCreated)}) ·{' '}
                  </>
                ) : null}
                Aujourd’hui :{' '}
                <strong className="text-ink">{syncStatus?.ordersToday.total ?? 0} commandes</strong> intégrées (dont{' '}
                {syncStatus?.ordersToday.byStatus.find((s) => s.status === 'processing')?.count ?? 0} en direct « En cours ») · Total base :{' '}
                {(syncStatus?.totalOrdersInDb ?? 0).toLocaleString('fr-FR')} commandes.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="xs"
            disabled={syncMutation.isPending || syncStatus?.isSyncRunning}
            onClick={() => syncMutation.mutate()}
          >
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                (syncMutation.isPending || syncStatus?.isSyncRunning) && 'animate-spin',
              )}
            />
            {syncMutation.isPending || syncStatus?.isSyncRunning
              ? 'Synchronisation en cours…'
              : 'Synchroniser maintenant'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Chiffre d&apos;Affaires Global
          </p>
          <p className="mt-1 font-mono text-xl font-bold text-ink">
            {formatTnd(totals.revenue)}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-ghost">sur la période sélectionnée</p>
        </div>
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Volume Unités Vendues
          </p>
          <p className="mt-1 font-mono text-xl font-bold text-ink">
            {totals.units.toLocaleString('fr-FR')}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-ghost">articles livrés ou en préparation</p>
        </div>
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Marque #1 en Volume
          </p>
          <p className="mt-1 font-bold text-base text-ink truncate">
            {topBrandByUnits?.brand ?? '—'}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-herb-700">
            {topBrandByUnits ? `${topBrandByUnits.qtySold.toLocaleString('fr-FR')} unités` : '—'}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Marque #1 en Chiffre d&apos;Affaires
          </p>
          <p className="mt-1 font-bold text-base text-ink truncate">
            {topBrandByRev?.brand ?? '—'}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-herb-700">
            {topBrandByRev ? formatTnd(topBrandByRev.revenueTtc) : '—'}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card/90 p-4 shadow-2xs">
          <p className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            Marques Actives
          </p>
          <p className="mt-1 font-mono text-xl font-bold text-ink">
            {totals.brandsCount.toLocaleString('fr-FR')}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-ghost">avec au moins 1 vente</p>
        </div>
      </div>

      {/* Filters and Search Bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card/80 p-4 shadow-xs">
        {/* Period mode */}
        <div className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          <span>Période</span>
          <div className="flex overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setPeriodMode('preset')}
              className={cn(
                'px-3 py-1.5 font-semibold transition-colors',
                periodMode === 'preset'
                  ? 'bg-herb-600 text-white'
                  : 'bg-card text-ink-soft hover:bg-muted',
              )}
            >
              Durées
            </button>
            <button
              type="button"
              onClick={() => setPeriodMode('custom')}
              className={cn(
                'px-3 py-1.5 font-semibold transition-colors',
                periodMode === 'custom'
                  ? 'bg-herb-600 text-white'
                  : 'bg-card text-ink-soft hover:bg-muted',
              )}
            >
              Dates
            </button>
          </div>
        </div>

        {periodMode === 'preset' ? (
          <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
            Durée
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
            >
              <option value={30}>30 derniers jours</option>
              <option value={90}>90 derniers jours</option>
              <option value={180}>180 derniers jours</option>
              <option value={365}>12 derniers mois</option>
            </select>
          </label>
        ) : (
          <>
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Du
              <input
                type="date"
                value={fromDate}
                min="2023-03-01"
                onChange={(e) => setFromDate(e.target.value)}
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
              />
            </label>
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Au
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
              />
            </label>
          </>
        )}

        {/* Sort */}
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Trier par
          <select
            value={sort}
            onChange={(e) =>
              setSort(
                e.target.value as 'qty' | 'revenue' | 'orders' | 'products',
              )
            }
            className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value="qty">Unités vendues (Volume)</option>
            <option value="revenue">Chiffre d&apos;affaires TTC (Valeur)</option>
            <option value="orders">Nombre de commandes</option>
            <option value="products">Nombre de références vendues</option>
          </select>
        </label>

        {/* Brand search */}
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Rechercher une marque
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ex: Avene, SVR, Phyteal…"
              className="h-9 min-w-56 rounded-lg border border-border bg-card pl-8 pr-3 text-xs font-semibold text-ink shadow-xs placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
            />
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost" />
          </div>
        </label>

        {/* Live Processing status toggle */}
        <label className="flex items-center gap-2 pb-2 text-xs font-medium text-ink cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeProcessing}
            onChange={(e) => setIncludeProcessing(e.target.checked)}
            className="h-4 w-4 rounded border-border text-herb-600 focus:ring-herb-500"
          />
          <span>Inclure commandes du jour (« En cours » / processing)</span>
        </label>

        <span className="flex-1" />

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={expandedBrands.size > 0 ? collapseAll : expandAll}
          >
            {expandedBrands.size > 0 ? 'Tout replier' : 'Tout déplier'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={brands.length === 0}
            onClick={exportCsv}
          >
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {brandQuery.isLoading ? (
          <div className="flex items-center justify-center p-12 text-xs text-ink-soft">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin text-herb-600" />
            Agrégation des ventes par marque en cours…
          </div>
        ) : brands.length === 0 ? (
          <div className="p-12 text-center text-xs text-ink-soft">
            Aucune vente trouvée pour les critères et filtres sélectionnés.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-border bg-muted/60 text-[10px] font-bold uppercase tracking-wider text-ink-soft">
                <tr>
                  <th className="px-4 py-3 text-center w-12">#</th>
                  <th className="px-4 py-3">Marque</th>
                  <th className="px-4 py-3 text-right">Unités Vendues</th>
                  <th className="px-4 py-3 text-right">CA TTC</th>
                  <th className="px-4 py-3 text-right">Part de Marché</th>
                  <th className="px-4 py-3 text-right">Commandes</th>
                  <th className="px-4 py-3 text-right">Panier Moyen</th>
                  <th className="px-4 py-3 text-right">Réf. Vendues / Total</th>
                  <th className="px-4 py-3 text-right">Stock Rayon</th>
                  <th className="px-4 py-3 text-center w-10">Détails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {brands.map((b, idx) => {
                  const isExpanded = expandedBrands.has(b.brand);
                  const rank = idx + 1;
                  const unitPercent = Math.min(100, Math.round((b.qtySold / maxUnits) * 100));

                  return (
                    <BrandRow
                      key={b.brand}
                      row={b}
                      rank={rank}
                      unitPercent={unitPercent}
                      isExpanded={isExpanded}
                      onToggle={() => toggleBrandExpand(b.brand)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BrandRow({
  row,
  rank,
  unitPercent,
  isExpanded,
  onToggle,
}: {
  readonly row: BrandSalesRow;
  readonly rank: number;
  readonly unitPercent: number;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}): React.ReactElement {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer transition-colors hover:bg-muted/40 select-none',
          isExpanded && 'bg-muted/30 font-medium',
        )}
      >
        {/* Rank badge */}
        <td className="px-4 py-3 text-center">
          {rank === 1 ? (
            <span className="grid h-6 w-6 place-items-center rounded-full bg-amber-400 font-bold text-amber-950 text-xs shadow-2xs mx-auto">
              1
            </span>
          ) : rank === 2 ? (
            <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-300 font-bold text-slate-800 text-xs shadow-2xs mx-auto">
              2
            </span>
          ) : rank === 3 ? (
            <span className="grid h-6 w-6 place-items-center rounded-full bg-amber-600 font-bold text-white text-xs shadow-2xs mx-auto">
              3
            </span>
          ) : (
            <span className="font-mono text-ink-ghost font-medium text-xs">
              {rank}
            </span>
          )}
        </td>

        {/* Brand name & Catalogue link */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-herb-100/70 border border-herb-600/20 text-xs font-bold text-herb-900"
              aria-hidden="true"
            >
              {row.brand.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="font-bold text-ink truncate text-sm">{row.brand}</p>
              <Link
                to={`/products?brand=${encodeURIComponent(row.brand)}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11px] text-herb-700 hover:underline"
              >
                Voir au catalogue
                <ExternalLink className="h-2.5 w-2.5" />
              </Link>
            </div>
          </div>
        </td>

        {/* Units Sold + Mini progress bar */}
        <td className="px-4 py-3 text-right">
          <p className="font-mono font-bold text-ink text-sm">
            {row.qtySold.toLocaleString('fr-FR')}
          </p>
          <div className="mt-1 flex items-center justify-end gap-1.5">
            <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-herb-600 rounded-full"
                style={{ width: `${unitPercent}%` }}
              />
            </div>
            <span className="text-[10px] text-ink-ghost font-mono">{unitPercent}%</span>
          </div>
        </td>

        {/* Revenue TTC */}
        <td className="px-4 py-3 text-right font-mono font-bold text-ink text-sm">
          {formatTnd(row.revenueTtc)}
        </td>

        {/* Market Share % */}
        <td className="px-4 py-3 text-right">
          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100/70 text-emerald-800 border border-emerald-600/20">
            {(row.revenueShare * 100).toFixed(1)}%
          </span>
        </td>

        {/* Orders Count */}
        <td className="px-4 py-3 text-right font-mono text-ink">
          {row.ordersCount.toLocaleString('fr-FR')}
        </td>

        {/* Average Order Value */}
        <td className="px-4 py-3 text-right font-mono text-ink-soft">
          {formatTnd(row.averageOrderValue)}
        </td>

        {/* Active references vs Catalog Total */}
        <td className="px-4 py-3 text-right font-mono text-ink">
          <span>{row.distinctProductsSold}</span>
          <span className="text-ink-ghost"> / {row.catalogProductsCount}</span>
        </td>

        {/* Stock */}
        <td className="px-4 py-3 text-right font-mono">
          {row.stockQtyTotal !== null ? (
            <span
              className={cn(
                'font-semibold',
                row.stockQtyTotal > 0 ? 'text-emerald-700' : 'text-rose-700',
              )}
            >
              {row.stockQtyTotal.toLocaleString('fr-FR')}
            </span>
          ) : (
            <span className="text-ink-ghost">—</span>
          )}
        </td>

        {/* Expand caret */}
        <td className="px-4 py-3 text-center text-ink-ghost">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 mx-auto" />
          ) : (
            <ChevronRight className="h-4 w-4 mx-auto" />
          )}
        </td>
      </tr>

      {/* Collapsible Accordion: Top 5 Best-Selling Products for this Brand */}
      {isExpanded && (
        <tr className="bg-muted/20">
          <td colSpan={10} className="px-6 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-ink">
                  Top Produits Phares de {row.brand}
                </p>
                <span className="text-[11px] text-ink-ghost">
                  {row.topProducts.length} référence(s) affichée(s)
                </span>
              </div>
              {row.topProducts.length === 0 ? (
                <p className="text-xs text-ink-ghost italic">
                  Aucun détail produit disponible pour cette marque.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {row.topProducts.map((p, pIdx) => (
                    <div
                      key={p.sku}
                      className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-2xs"
                    >
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-bold text-ink-soft">
                        {pIdx + 1}
                      </span>
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-md object-cover border border-border/80"
                        />
                      ) : (
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-muted text-ink-ghost border border-border/60">
                          <Package className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-xs text-ink truncate" title={p.name}>
                          {p.name}
                        </p>
                        <p className="font-mono text-[10px] text-ink-ghost">{p.sku}</p>
                        <div className="mt-1 flex items-center justify-between text-xs">
                          <span className="font-mono font-bold text-herb-700">
                            {p.qtySold} vendus
                          </span>
                          <span className="font-mono text-ink-soft font-medium">
                            {formatTnd(p.revenueTtc)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
